/**
 * Cognito SRP auth client.
 *
 * Direct user-pool authentication via amazon-cognito-identity-js — no
 * redirect dance, no hosted UI. The PWA renders its own email/password
 * form, exchanges credentials for tokens, and stores them in the shared
 * auth-store. Mirrors the working pattern in geminilogic-partner-portal.
 *
 * Vite env vars (set at build time, baked into the bundle):
 *
 *   VITE_AUTH_MODE             = 'cognito' to activate; anything else
 *                                falls back to the dev IDP picker.
 *   VITE_COGNITO_USER_POOL_ID  = e.g. ca-central-1_AbCdEfGhI
 *   VITE_COGNITO_CLIENT_ID     = the SPA app client (public, no secret)
 *
 * Tokens we receive from Cognito:
 *
 *   - idToken (ID token, RS256-signed)    -> the JWT we hand to the API
 *                                            as Bearer. Claims include
 *                                            sub, email, cognito:groups.
 *   - accessToken (Access token)          -> not used by our API today
 *   - refreshToken                        -> kept by the SDK, used to
 *                                            silently re-issue when the
 *                                            idToken expires (~1h).
 *
 * We attach the idToken (NOT the access token) because the JwtAuthGuard
 * expects user identity claims (sub, email, group) which Cognito puts on
 * the ID token. The access token only carries scopes/clientId.
 */

import {
  AuthenticationDetails,
  CognitoUser,
  CognitoUserAttribute,
  CognitoUserPool,
  type CognitoUserSession,
} from 'amazon-cognito-identity-js';
import type { AuthUser } from './auth-store.js';

type AuthMode = 'cognito' | 'dev';

const env = import.meta.env;

export const authMode: AuthMode = env.VITE_AUTH_MODE === 'cognito' ? 'cognito' : 'dev';

let cachedPool: CognitoUserPool | null = null;

function getPool(): CognitoUserPool {
  if (cachedPool) return cachedPool;
  const userPoolId = env.VITE_COGNITO_USER_POOL_ID as string | undefined;
  const clientId = env.VITE_COGNITO_CLIENT_ID as string | undefined;
  if (!userPoolId || !clientId) {
    throw new Error(
      'VITE_AUTH_MODE=cognito requires VITE_COGNITO_USER_POOL_ID + VITE_COGNITO_CLIENT_ID.',
    );
  }
  cachedPool = new CognitoUserPool({ UserPoolId: userPoolId, ClientId: clientId });
  return cachedPool;
}

interface CompletedSession {
  token: string;
  expiresIn: number;
  user: AuthUser;
}

export interface LoginSession {
  kind: 'session';
  token: string;
  expiresIn: number;
  user: AuthUser;
}

export interface NewPasswordChallenge {
  kind: 'new-password-required';
  /** Pass the user-chosen new password to completeNewPassword(). */
  complete: (newPassword: string) => Promise<CompletedSession>;
  /** What Cognito told us was missing (usually just nothing). */
  requiredAttributes: string[];
}

export type LoginResult = LoginSession | NewPasswordChallenge;

/**
 * Authenticate against Cognito via SRP. On success returns a session
 * ready to drop into useAuth.setSession; on FORCE_CHANGE_PASSWORD
 * returns a follow-up callback the UI invokes after the user picks
 * a permanent password.
 */
export function cognitoLogin(
  email: string,
  password: string,
): Promise<LoginResult> {
  const pool = getPool();
  const cognitoUser = new CognitoUser({ Username: email, Pool: pool });
  // SRP requires AuthFlow USER_SRP_AUTH which is the SDK default.
  cognitoUser.setAuthenticationFlowType('USER_SRP_AUTH');
  const authDetails = new AuthenticationDetails({
    Username: email,
    Password: password,
  });

  return new Promise((resolve, reject) => {
    cognitoUser.authenticateUser(authDetails, {
      onSuccess: (session) => {
        try {
          resolve({ kind: 'session', ...sessionToCompleted(session) });
        } catch (err) {
          reject(err);
        }
      },
      onFailure: (err) => {
        reject(err instanceof Error ? err : new Error(String(err)));
      },
      newPasswordRequired: (userAttributes, requiredAttributes) => {
        // Cognito strips back two attributes that aren't user-settable;
        // remove them before responding.
        delete userAttributes.email_verified;
        delete userAttributes.email;
        resolve({
          kind: 'new-password-required',
          requiredAttributes: requiredAttributes ?? [],
          complete: (newPassword) =>
            new Promise<CompletedSession>((res, rej) => {
              cognitoUser.completeNewPasswordChallenge(
                newPassword,
                userAttributes,
                {
                  onSuccess: (session) => {
                    try {
                      res(sessionToCompleted(session));
                    } catch (err) {
                      rej(err);
                    }
                  },
                  onFailure: (err) => rej(err),
                },
              );
            }),
        });
      },
    });
  });
}

/**
 * Restore an existing Cognito session (called at app bootstrap so a
 * refresh doesn't bounce the user back to /login). Returns null when
 * no cached session exists; throws on a session that's invalid in some
 * other way.
 */
export function tryRestoreCognitoSession(): Promise<CompletedSession | null> {
  const pool = getPool();
  const current = pool.getCurrentUser();
  if (!current) return Promise.resolve(null);
  return new Promise((resolve, reject) => {
    current.getSession((err: Error | null, session: CognitoUserSession | null) => {
      if (err) {
        reject(err);
        return;
      }
      if (!session?.isValid()) {
        resolve(null);
        return;
      }
      try {
        resolve(sessionToCompleted(session));
      } catch (innerErr) {
        reject(innerErr);
      }
    });
  });
}

export function signOutCognito(): void {
  const current = getPool().getCurrentUser();
  if (current) current.signOut();
}

/**
 * Start the self-service password reset. Pools here use email-as-username
 * with email_verified=true, so Cognito emails a 6-digit code. Resolves
 * once the code is SENT: the SDK invokes inputVerificationCode for that —
 * onSuccess in this callback set only fires after confirmPassword, so it
 * must NOT be where we resolve.
 */
export function requestPasswordReset(email: string): Promise<void> {
  const cognitoUser = new CognitoUser({ Username: email, Pool: getPool() });
  return new Promise((resolve, reject) => {
    cognitoUser.forgotPassword({
      onSuccess: () => resolve(),
      onFailure: (err) => reject(friendlyResetError(err)),
      inputVerificationCode: () => resolve(),
    });
  });
}

/** Complete the reset with the emailed code + the user's chosen password. */
export function confirmPasswordReset(
  email: string,
  code: string,
  newPassword: string,
): Promise<void> {
  const cognitoUser = new CognitoUser({ Username: email, Pool: getPool() });
  return new Promise((resolve, reject) => {
    cognitoUser.confirmPassword(code, newPassword, {
      onSuccess: () => resolve(),
      onFailure: (err) => reject(friendlyResetError(err)),
    });
  });
}

/** Map Cognito's exception codes to copy a field user can act on.
 *  SDK errors carry the exception name on `code` (and `name`). */
function friendlyResetError(err: unknown): Error {
  const code =
    typeof err === 'object' && err !== null
      ? ((err as { code?: string; name?: string }).code ??
        (err as { name?: string }).name)
      : undefined;
  switch (code) {
    case 'UserNotFoundException':
      return new Error('No account with that email.');
    case 'CodeMismatchException':
      return new Error("That code didn't match — check the email and try again.");
    case 'ExpiredCodeException':
      return new Error('That code expired — request a new one.');
    case 'LimitExceededException':
      return new Error('Too many attempts — wait a few minutes.');
    default:
      return err instanceof Error ? err : new Error(String(err));
  }
}

/** Translate a CognitoUserSession into the shape our auth-store wants. */
function sessionToCompleted(session: CognitoUserSession): CompletedSession {
  const idToken = session.getIdToken();
  const jwt = idToken.getJwtToken();
  const payload = idToken.decodePayload();

  // Cognito idToken contains:
  //   sub                  = user's UUID (Cognito's user ID)
  //   email                = user's email
  //   cognito:groups       = array of group names this user belongs to
  //   exp                  = expiry epoch seconds
  // Our AuthUser shape needs id/email/displayName/role.
  const sub = stringClaim(payload, 'sub') ?? '';
  const email = stringClaim(payload, 'email') ?? '';
  const role = mapCognitoRole(payload['cognito:groups']);
  const exp = typeof payload.exp === 'number' ? payload.exp : 0;
  const expiresIn = Math.max(60, exp - Math.floor(Date.now() / 1000));

  return {
    token: jwt,
    expiresIn,
    user: {
      id: sub,
      email,
      displayName:
        stringClaim(payload, 'name') ??
        stringClaim(payload, 'preferred_username') ??
        email,
      role,
    },
  };
}

function stringClaim(payload: Record<string, unknown>, key: string): string | null {
  const v = payload[key];
  return typeof v === 'string' && v.length > 0 ? v : null;
}

function mapCognitoRole(groups: unknown): AuthUser['role'] {
  // cognito:groups arrives as a JSON array on the idToken payload.
  if (!Array.isArray(groups)) return 'field_tech';
  for (const g of groups) {
    if (g === 'admin' || g === 'dispatcher' || g === 'office' || g === 'field_tech') {
      return g;
    }
  }
  return 'field_tech'; // least-privilege default — match the API's fallback
}

/** Currently-required attribute on a forced-change challenge (rare; we
 *  ask the user to set their name when present). Exposed so the login
 *  form can render an extra field if Cognito demands it. */
export function attributeLabel(attr: string): string {
  switch (attr) {
    case 'name':
      return 'Full name';
    case 'given_name':
      return 'First name';
    case 'family_name':
      return 'Last name';
    default:
      return attr;
  }
}

/** Helper for callers that want to also push a new user attribute
 *  during the forced-password-change flow. Unused by the current login
 *  UI but kept for completeness with Partner Portal's pattern. */
export function buildAttributes(
  required: ReadonlyArray<string>,
  values: Record<string, string>,
): CognitoUserAttribute[] {
  return required
    .map((name) => {
      const value = values[name];
      if (value === undefined) return null;
      return new CognitoUserAttribute({ Name: name, Value: value });
    })
    .filter((a): a is CognitoUserAttribute => a !== null);
}
